import axios, { type AxiosRequestConfig } from 'axios'
import { reportingIdentifier } from 'src/queries/reporting/reporting.identifier'
import {
  type DraftDisplayProjection,
  type DraftSubmissionProjection,
  type MessageSubmissionProjection,
  REPORTING_AGENT_VERSION,
  type ReportingChartQuestionContextRef,
  type ReportingMessagePage,
  type ReportingRunProjection,
  type SavedReportDetail,
  type SavedReportPage,
} from 'src/queries/reportingAgent/reportingAgent.models'
import {
  parseDraftDisplayProjection,
  parseDraftSubmissionProjection,
  parseMessagePage,
  parseMessageSubmissionProjection,
  parseRunProjection,
  parseSavedReportDetail,
  parseSavedReportPage,
} from 'src/queries/reportingAgent/reportingAgent.validation'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'

export interface OrganizationScope {
  organizationId: string
}

export interface CreateReportingDraftRequest extends OrganizationScope {
  question: string
  idempotencyKey: string
  chartContext?: ReportingChartQuestionContextRef
}

export interface ResolveReportingDraftRequest extends OrganizationScope {
  draftRequestId: string
}

export interface ListReportingMessagesRequest extends OrganizationScope {
  conversationId: string
  limit?: number
  after?: string
}

export interface SendReportingMessageRequest extends OrganizationScope {
  conversationId: string
  content: string
  referencedArtifactId?: string
  idempotencyKey: string
  chartContext?: ReportingChartQuestionContextRef
}

export interface ReportingRunRequest extends OrganizationScope {
  runId: string
}

export interface ReportingDraftDisplayRequest extends OrganizationScope {
  conversationId: string
  artifactId: string
}

export interface CreateSavedReportingReportRequest extends OrganizationScope {
  title: string
  artifactId: string
  idempotencyKey: string
}

export interface ListSavedReportingReportsRequest extends OrganizationScope {
  limit?: number
  after?: string
}

export interface SavedReportingReportRequest extends OrganizationScope {
  reportId: string
}

const nonEmpty = (value: string, name: string): string => {
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    value.trim() !== value
  ) {
    throw new Error(`${name} must be a non-empty string`)
  }
  return value
}

const pathSegment = (value: string, name: string): string => {
  return reportingIdentifier(value, name)
}

const organization = (value: string): string =>
  pathSegment(value, 'organizationId')

const limit = (value: number | undefined): number | undefined => {
  if (value === undefined) return undefined
  if (!Number.isInteger(value) || value < 1 || value > 50)
    throw new Error('limit must be between 1 and 50')
  return value
}

const idempotency = (value: string): string => {
  const result = nonEmpty(value, 'idempotencyKey')
  if (result.length > 256)
    throw new Error('idempotencyKey must be at most 256 characters')
  return result
}

const config = (
  organizationId: string,
  idempotencyKey?: string,
): AxiosRequestConfig => {
  const requestConfig = getRequestConfig(RequestConfig.Agent)
  return {
    ...requestConfig,
    params: { organization_id: organizationId },
    ...(idempotencyKey === undefined
      ? {}
      : {
          headers: {
            ...requestConfig.headers,
            'Idempotency-Key': idempotencyKey,
          },
        }),
  }
}

export const createReportingDraft = async ({
  organizationId,
  question,
  idempotencyKey,
  chartContext,
}: CreateReportingDraftRequest): Promise<DraftSubmissionProjection> => {
  const org = organization(organizationId)
  const prompt = nonEmpty(question, 'question')
  const response = await axios.post(
    '/agents/reporting/drafts',
    {
      version: REPORTING_AGENT_VERSION,
      question: prompt,
      ...(chartContext ? { chart_context: chartContext } : {}),
    },
    config(org, idempotency(idempotencyKey)),
  )
  return parseDraftSubmissionProjection(response.data)
}

export const resolveReportingDraft = async ({
  organizationId,
  draftRequestId,
}: ResolveReportingDraftRequest): Promise<DraftSubmissionProjection> => {
  const org = organization(organizationId)
  const requestId = pathSegment(draftRequestId, 'draftRequestId')
  const response = await axios.get(
    `/agents/reporting/drafts/requests/${requestId}`,
    config(org),
  )
  return parseDraftSubmissionProjection(response.data)
}

export const listReportingMessages = async ({
  organizationId,
  conversationId,
  limit: requestedLimit,
  after,
}: ListReportingMessagesRequest): Promise<ReportingMessagePage> => {
  const org = organization(organizationId)
  const conversation = pathSegment(conversationId, 'conversationId')
  const requestConfig = config(org)
  requestConfig.params = {
    ...requestConfig.params,
    ...(limit(requestedLimit) === undefined ? {} : { limit: requestedLimit }),
    ...(after === undefined ? {} : { after }),
  }
  const response = await axios.get(
    `/agents/conversations/${conversation}/messages`,
    requestConfig,
  )
  return parseMessagePage(response.data)
}

export const sendReportingMessage = async ({
  organizationId,
  conversationId,
  content,
  referencedArtifactId,
  idempotencyKey,
  chartContext,
}: SendReportingMessageRequest): Promise<MessageSubmissionProjection> => {
  const org = organization(organizationId)
  const conversation = pathSegment(conversationId, 'conversationId')
  const message = nonEmpty(content, 'content')
  const body = {
    version: REPORTING_AGENT_VERSION,
    content: message,
    ...(referencedArtifactId === undefined
      ? {}
      : {
          referenced_artifact_id: pathSegment(
            referencedArtifactId,
            'referencedArtifactId',
          ),
        }),
    ...(chartContext ? { chart_context: chartContext } : {}),
  }
  const response = await axios.post(
    `/agents/conversations/${conversation}/messages`,
    body,
    config(org, idempotency(idempotencyKey)),
  )
  return parseMessageSubmissionProjection(response.data)
}

export const getReportingRun = async ({
  organizationId,
  runId,
}: ReportingRunRequest): Promise<ReportingRunProjection> => {
  const response = await axios.get(
    `/agents/runs/${pathSegment(runId, 'runId')}`,
    config(organization(organizationId)),
  )
  return parseRunProjection(response.data)
}

export const getReportingDraftDisplay = async ({
  organizationId,
  conversationId,
  artifactId,
}: ReportingDraftDisplayRequest): Promise<DraftDisplayProjection> => {
  const response = await axios.get(
    `/agents/conversations/${pathSegment(conversationId, 'conversationId')}/artifacts/${pathSegment(artifactId, 'artifactId')}/display`,
    config(organization(organizationId)),
  )
  return parseDraftDisplayProjection(response.data)
}

export const createSavedReportingReport = async ({
  organizationId,
  title,
  artifactId,
  idempotencyKey,
}: CreateSavedReportingReportRequest): Promise<SavedReportDetail> => {
  const response = await axios.post(
    '/agents/reporting/reports',
    {
      version: REPORTING_AGENT_VERSION,
      title: nonEmpty(title, 'title'),
      artifact_id: pathSegment(artifactId, 'artifactId'),
    },
    config(organization(organizationId), idempotency(idempotencyKey)),
  )
  return parseSavedReportDetail(response.data)
}

export const listSavedReportingReports = async ({
  organizationId,
  limit: requestedLimit,
  after,
}: ListSavedReportingReportsRequest): Promise<SavedReportPage> => {
  const requestConfig = config(organization(organizationId))
  requestConfig.params = {
    ...requestConfig.params,
    ...(limit(requestedLimit) === undefined ? {} : { limit: requestedLimit }),
    ...(after === undefined ? {} : { after }),
  }
  const response = await axios.get('/agents/reporting/reports', requestConfig)
  return parseSavedReportPage(response.data)
}

export const getSavedReportingReport = async ({
  organizationId,
  reportId,
}: SavedReportingReportRequest): Promise<SavedReportDetail> => {
  const response = await axios.get(
    `/agents/reporting/reports/${pathSegment(reportId, 'reportId')}`,
    config(organization(organizationId)),
  )
  return parseSavedReportDetail(response.data)
}

// Naming aliases keep the transport boundary easy to consume from query and
// mutation layers without creating alternate request shapes or endpoints.
export const submitReportingDraft = createReportingDraft
export const fetchReportingDraftRequest = resolveReportingDraft
export const fetchReportingMessages = listReportingMessages
export const postReportingMessage = sendReportingMessage
export const fetchReportingRun = getReportingRun
export const fetchReportingDraftDisplay = getReportingDraftDisplay
export const saveReportingReport = createSavedReportingReport
export const fetchSavedReportingReports = listSavedReportingReports
export const fetchSavedReportingReport = getSavedReportingReport
